import DropdownSheet from 'LuxKit/Dropdown/Sheet/DropdownSheet'
import { GlobalSearchDispatchContext, GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import { GlobalSearchStateActions } from 'contexts/GlobalSearch/GlobalSearchState'
import React, { RefObject, useCallback, useContext, useMemo, useRef, useState, useImperativeHandle, forwardRef } from 'react'
import { useIntl } from 'react-intl'
import CruiseLinesSearchList from './CruiseLineSelectContentList'
import FormattedCruiseLineSearchSummary from './FormattedCruiseLineSearchSummary'
import SearchFormTextField from 'components/SearchV2/Components/SearchFormField/SearchFormTextField'
import useToggle from 'hooks/useToggle'
import * as Analytics from 'analytics/analytics'
import { mapCruiseLineFilterToSnowplowSearchEvent } from 'analytics/mapSnowplowSearchTracking'
import { searchEventWithContext } from 'analytics/snowplow/events'

export interface CruiseLineSelectDesktopRef {
  focus: () => void
}

interface Props {
  cruiseLineNames: Array<string>
  dropdownAnchorRef: RefObject<HTMLElement | null>
  onChange: (values: App.CruiseGlobalFilters) => void
  handlerSearchTerm: (e: React.ChangeEvent<HTMLInputElement>) => void
  handlerClearSearchTerm: () => void
  searchTerm: string
}

const CruiseLineSelectDesktop = forwardRef<CruiseLineSelectDesktopRef, Props>((props, ref) => {
  const {
    cruiseLineNames,
    onChange,
    dropdownAnchorRef,
    handlerSearchTerm,
    handlerClearSearchTerm,
    searchTerm,
  } = props
  const intl = useIntl()
  const inputTriggerRef = useRef<HTMLInputElement>(null)
  const { value: isOpen, on: openDropdown, off: closeDropdown } = useToggle(false)
  const [showAll, setShowAll] = useState(false)
  const { cruiseLines } = useContext(GlobalSearchStateContext)
  const searchDispatch = useContext(GlobalSearchDispatchContext)
  const globalSearchState = useContext(GlobalSearchStateContext)

  const onApply = useCallback(() => {
    Analytics.trackClientEvent({
      subject: 'filter_applied',
      action: 'applied',
      category: 'cruise_line_filter',
      type: 'interaction',
    })

    Analytics.trackEvent(searchEventWithContext({
      searchEvent: { type: 'filters' },
      sortContext: { value: 'recommended' },
      searchContext: mapCruiseLineFilterToSnowplowSearchEvent(globalSearchState),
      filterContexts: cruiseLines.map(cruiseLine => ({
        label: 'Cruise Line',
        type: 'cruise_line',
        value: cruiseLine,
      })),
    }))

    onChange({ cruiseLines })
    closeDropdown()
  }, [onChange, cruiseLines, closeDropdown, globalSearchState])

  const onClear = useCallback(() => {
    Analytics.trackClientEvent({
      subject: 'filter_cleared',
      action: 'cleared',
      category: 'cruise_line_filter',
      type: 'interaction',
    })
    searchDispatch({ type: GlobalSearchStateActions.SET_CRUISE_LINES, cruiseLines: [] })
    onChange({ cruiseLines: [] })
    setShowAll(false)
    handlerClearSearchTerm()
  }, [handlerClearSearchTerm, onChange, searchDispatch])

  const onOpen = useCallback(() => {
    Analytics.trackClientEvent({
      subject: 'filter_opened',
      action: 'opened',
      category: 'cruise_line_filter',
      type: 'interaction',
    })
    openDropdown()
  }, [openDropdown])

  const cruiseLineLabel = useMemo(() => {
    if (isOpen) return searchTerm

    const [firstName] = cruiseLineNames
    const more = cruiseLineNames.length > 1 ? `+${cruiseLineNames.length - 1}` : ''
    return firstName && `${firstName} ${more}`
  }, [cruiseLineNames, isOpen, searchTerm])

  useImperativeHandle(ref, () => ({
    focus: () => {
      inputTriggerRef.current?.focus()
      openDropdown()
    },
  }), [openDropdown])

  const cruiseLinesLabel = intl.formatMessage({
    id: 'SOLpn91gmD',
    defaultMessage: 'Cruise lines',
    description: 'Label for cruise lines filter input',
  })

  const searchCruiseLinesPlaceholder = intl.formatMessage({
    id: 'w590xcVP5P',
    defaultMessage: 'Search cruise lines',
    description: 'Placeholder text when searching for cruise lines',
  })

  const allCruiseLinesPlaceholder = intl.formatMessage({
    id: '5Hl4eDUzl2',
    defaultMessage: 'All cruise lines',
    description: 'Placeholder text showing all cruise lines are selected',
  })

  const applyButtonText = intl.formatMessage({
    id: 'zil6vuq6k2',
    defaultMessage: 'Apply',
    description: 'Apply button text for cruise line filter',
  })

  const resetAllButtonText = intl.formatMessage({
    id: '+4ABAO7MqK',
    defaultMessage: 'Reset all',
    description: 'Reset all button text for cruise line filter',
  })

  return (
    <div data-testid="cruise-line-select-desktop">
      <SearchFormTextField
        onClick={onOpen}
        ref={inputTriggerRef}
        label={cruiseLinesLabel}
        placeholder={isOpen ? searchCruiseLinesPlaceholder : allCruiseLinesPlaceholder}
        value={cruiseLineLabel}
        displayValue={cruiseLineLabel}
        onChange={handlerSearchTerm}
        onClear={handlerClearSearchTerm}
      />

      <DropdownSheet
        size="fill-anchor"
        data-testid="cruise-line-select-dropdown"
        anchorRef={dropdownAnchorRef}
        triggerRef={inputTriggerRef}
        open={isOpen}
        onClose={closeDropdown}
        primaryActionProps={{
          'data-testid': 'cruise-filter-footer-apply',
          children: applyButtonText,
          onClick: onApply,
        }}
        secondaryActionProps={{
          'data-testid': 'cruise-filter-footer-clear-all',
          kind: 'tertiary',
          children: resetAllButtonText,
          onClick: onClear,
        }}
        footerStart={<FormattedCruiseLineSearchSummary />}
      >
        <CruiseLinesSearchList
          showAll={showAll}
          onSetShowAll={setShowAll}
          searchTerm={searchTerm}
        />
      </DropdownSheet>
    </div>
  )
})

CruiseLineSelectDesktop.displayName = 'CruiseLineSelectDesktop'

export default CruiseLineSelectDesktop
